"use strict";
import { FEWTOKEN_DEFAULT_ACTIVE_LIST_URLS, RING_DEFAULT_ACTIVE_LIST_URLS } from "constants/lists";
import { atom, useAtom } from "jotai";
import { useCallback, useEffect, useMemo } from "react";
import { useAllLists } from "state/lists/hooks";
import { WrappedTokenInfo } from "state/lists/wrappedTokenInfo";
import { COMMON_BASES } from "uniswap/src/constants/routing";
import { ProtectionResult } from "uniswap/src/data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { TokenList } from "uniswap/src/features/dataApi/types";
import { buildCurrencyInfo } from "uniswap/src/features/dataApi/utils";
import { currencyId } from "uniswap/src/utils/currencyId";
const localOnchainCurrencyInfosAtom = atom({});
export function useRegisterTokenSelectorCurrencies(currency0, currency1) {
  const [, setLocalOnchainCurrencyInfos] = useAtom(localOnchainCurrencyInfosAtom);
  useEffect(() => {
    const currencies = [currency0, currency1].filter((currency) => Boolean(currency?.isToken));
    if (!currencies.length) {
      return;
    }
    setLocalOnchainCurrencyInfos((current) => {
      const next = { ...current };
      for (const currency of currencies) {
        next[currencyId(currency).toLowerCase()] = buildCurrencyInfo({
          currency,
          currencyId: currencyId(currency),
          logoUrl: null,
          safetyInfo: {
            tokenList: TokenList.NonDefault,
            protectionResult: ProtectionResult.Benign
          }
        });
      }
      return next;
    });
  }, [currency0, currency1, setLocalOnchainCurrencyInfos]);
}
function searchRank(token, query) {
  const symbol = token.currency.symbol?.toLowerCase() ?? "";
  const name = token.currency.name?.toLowerCase() ?? "";
  const address = token.currency.isToken ? token.currency.address.toLowerCase() : "";
  if (symbol === query) {
    return 0;
  }
  if (name === query) {
    return 1;
  }
  if (symbol.startsWith(query)) {
    return 2;
  }
  if (name.startsWith(query)) {
    return 3;
  }
  if (address.startsWith(query)) {
    return 4;
  }
  if (symbol.includes(query)) {
    return 5;
  }
  if (name.includes(query)) {
    return 6;
  }
  return 7;
}
export function useTokenSelectorListSearch() {
  const lists = useAllLists();
  const [localOnchainCurrencyInfos] = useAtom(localOnchainCurrencyInfosAtom);
  const localOnchainCurrencies = useMemo(() => Object.values(localOnchainCurrencyInfos), [localOnchainCurrencyInfos]);
  const activeListEntries = useMemo(() => {
    const entries = [];
    const seen = /* @__PURE__ */ new Set();
    for (const url of [...RING_DEFAULT_ACTIVE_LIST_URLS, ...FEWTOKEN_DEFAULT_ACTIVE_LIST_URLS]) {
      const current = lists[url]?.current;
      if (!current) {
        continue;
      }
      for (const info of current.tokens) {
        const candidates = [info];
        const fewWrappedAddress = info.extensions?.fewWrappedAddress;
        if (typeof fewWrappedAddress === "string") {
          candidates.push({
            ...info,
            address: fewWrappedAddress,
            name: typeof info.extensions?.fewName === "string" ? info.extensions.fewName : `Few Wrapped ${info.name}`,
            symbol: typeof info.extensions?.fewSymbol === "string" ? info.extensions.fewSymbol : `fw${info.symbol}`,
            extensions: void 0
          });
        }
        for (const candidate of candidates) {
          const key = `${candidate.chainId}:${candidate.address.toLowerCase()}`;
          if (seen.has(key)) {
            continue;
          }
          seen.add(key);
          entries.push({ info: candidate, list: current });
        }
      }
    }
    return entries;
  }, [lists]);
  return useCallback(
    ({ chainFilter, searchFilter }) => {
      const normalizedQuery = searchFilter?.trim().toLowerCase();
      if (!normalizedQuery) {
        return void 0;
      }
      const isAddressQuery = normalizedQuery.startsWith("0x");
      const filtered = [];
      const commonBases = chainFilter ? COMMON_BASES[chainFilter] ?? [] : Object.values(COMMON_BASES).flat();
      for (const currencyInfo of commonBases) {
        const { currency } = currencyInfo;
        const symbol = currency.symbol?.toLowerCase() ?? "";
        const name = currency.name?.toLowerCase() ?? "";
        const address = currency.isToken ? currency.address.toLowerCase() : "";
        const matchesQuery = isAddressQuery ? address.startsWith(normalizedQuery) : symbol.includes(normalizedQuery) || name.includes(normalizedQuery);
        if (matchesQuery) {
          filtered.push(currencyInfo);
        }
      }
      for (const currencyInfo of localOnchainCurrencies) {
        const { currency } = currencyInfo;
        const symbol = currency.symbol?.toLowerCase() ?? "";
        const name = currency.name?.toLowerCase() ?? "";
        const address = currency.isToken ? currency.address.toLowerCase() : "";
        const matchesQuery = isAddressQuery ? address.startsWith(normalizedQuery) : symbol.includes(normalizedQuery) || name.includes(normalizedQuery);
        if (!matchesQuery || chainFilter && !isAddressQuery && currency.chainId !== chainFilter) {
          continue;
        }
        const key = currencyId(currency).toLowerCase();
        if (filtered.some((item) => currencyId(item.currency).toLowerCase() === key)) {
          continue;
        }
        filtered.push({
          ...currencyInfo,
          isFromOtherNetwork: Boolean(isAddressQuery && chainFilter && currency.chainId !== chainFilter)
        });
      }
      for (const { info, list } of activeListEntries) {
        const symbol = info.symbol?.toLowerCase() ?? "";
        const name = info.name?.toLowerCase() ?? "";
        const address = info.address.toLowerCase();
        const matchesQuery = isAddressQuery ? address.startsWith(normalizedQuery) : symbol.includes(normalizedQuery) || name.includes(normalizedQuery);
        if (!matchesQuery) {
          continue;
        }
        if (chainFilter && !isAddressQuery && info.chainId !== chainFilter) {
          continue;
        }
        if (filtered.some(
          ({ currency }) => currency.chainId === info.chainId && currency.isToken && currency.address.toLowerCase() === address
        )) {
          continue;
        }
        try {
          const token = new WrappedTokenInfo(info, list);
          filtered.push(
            buildCurrencyInfo({
              currency: token,
              currencyId: currencyId(token),
              logoUrl: token.logoURI,
              safetyInfo: {
                tokenList: TokenList.Default,
                protectionResult: ProtectionResult.Benign
              },
              isFromOtherNetwork: Boolean(isAddressQuery && chainFilter && token.chainId !== chainFilter)
            })
          );
        } catch {
          continue;
        }
      }
      if (!filtered.length) {
        return void 0;
      }
      return filtered.sort((a, b) => searchRank(a, normalizedQuery) - searchRank(b, normalizedQuery));
    },
    [activeListEntries, localOnchainCurrencies]
  );
}
